// b13: kortet Vädret vid huset (eller vid båten): väderstationen uppmätt, bredvid prognosen.
// Vindriktningen visas också som väderstreck, och källan står på kortet (Ecowitt eller BTHome).
// Vid misstänkt is visas prognosens vind, och kortet säger varför.
//
// b16: temperaturen och fukten ute visas oavsett källa (stationen, en givare eller ägarens entitet
// i Home Assistant), och kortet skriver källan.

import { dagOchTid, sedan, tal, type Vaderstation, vaderstreck } from "@batkoll/gemensam";
import { Kort, Rad } from "../komponenter.tsx";

const FINNS_INTE = "finns inte";

function varde(v: number | null, enhet: string, d = 1): string {
  return v === null ? FINNS_INTE : `${tal(v, d)} ${enhet}`.trim();
}

function riktning(grader: number | null): string {
  return grader === null ? FINNS_INTE : `${vaderstreck(grader)} (${tal(grader, 0)}°)`;
}

export function Vaderkort(props: { v: Vaderstation; bat: boolean; tidszon: string; nu: number }) {
  const { v, nu } = props;
  const p = v.prognos;
  const gammalt = nu - v.tid > 30 * 60_000;
  const ton = v.is || v.lagaBatterier.length > 0 ? "varning" : undefined;
  return (
    <Kort rubrik={props.bat ? "Vädret vid båten" : "Vädret vid huset"} ton={ton}>
      <p class="liten">
        Uppmätt av väderstationen: {v.kalla}, {gammalt ? "senast " : ""}
        {sedan(v.tid, nu)}.
      </p>
      <table class="tabell vader" aria-label="Uppmätt och prognos">
        <thead>
          <tr>
            <th />
            <th scope="col">Uppmätt</th>
            <th scope="col">Prognos</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Temperatur</th>
            <td>{varde(v.temp, "°C")}</td>
            <td>{p ? varde(p.temp, "°C") : FINNS_INTE}</td>
          </tr>
          <tr>
            <th scope="row">Vind</th>
            <td>{v.vindkalla === "prognos" ? "misstänkt is" : varde(v.vind, "m/s")}</td>
            <td>{p ? varde(p.vind, "m/s") : FINNS_INTE}</td>
          </tr>
          <tr>
            <th scope="row">Byvind</th>
            <td>{v.vindkalla === "prognos" ? "misstänkt is" : varde(v.byvind, "m/s")}</td>
            <td>{p ? varde(p.byvind, "m/s") : FINNS_INTE}</td>
          </tr>
          <tr>
            <th scope="row">Vindriktning</th>
            <td>{v.vindkalla === "prognos" ? "misstänkt is" : riktning(v.riktning)}</td>
            <td>{p ? riktning(p.riktning) : FINNS_INTE}</td>
          </tr>
        </tbody>
      </table>
      {v.is && (
        <p class="meddelande" role="status">
          {v.is.text} Vinden{" "}
          {v.vind === null
            ? "finns inte i prognosen"
            : `enligt prognosen: ${varde(v.vind, "m/s")}, byar ${varde(v.byvind, "m/s")}, från ${riktning(v.riktning)}`}
          .
        </p>
      )}
      <Rad namn="Fukt">{varde(v.fukt, "%", 0)}</Rad>
      {(v.tempKalla || v.fuktKalla) && (
        <p class="liten">
          {v.tempKalla && v.tempKalla === v.fuktKalla
            ? `Temperaturen och fukten ute: ${v.tempKalla}.`
            : [
                v.tempKalla ? `Temperaturen ute: ${v.tempKalla}.` : null,
                v.fuktKalla ? `Fukten ute: ${v.fuktKalla}.` : null,
              ]
                .filter((x) => x !== null)
                .join(" ")}
        </p>
      )}
      <Rad namn="Regn">
        {varde(v.regn, "mm/h")}
        {v.regnar === true ? ", det regnar" : ""}
      </Rad>
      <Rad namn="Regn i dag">{varde(v.regnDygn, "mm")}</Rad>
      <Rad namn="Lufttryck">{varde(v.tryck, "hPa", 0)}</Rad>
      <Rad namn="Solinstrålning">{varde(v.ljus, "W/m²", 0)}</Rad>
      <Rad namn="UV-index">{varde(v.uv, "", 0)}</Rad>
      {v.lagaBatterier.length > 0 && (
        <p class="meddelande">Lågt batteri: {v.lagaBatterier.join(", ")}.</p>
      )}
      {p && (
        <p class="liten">
          Prognos: {p.kalla}, för {dagOchTid(p.tid, props.tidszon, nu)}.
        </p>
      )}
    </Kort>
  );
}
